const cache = new Map<string, Promise<string>>()

const BG_THRESHOLD = 232
const EDGE_SOFT_START = 200

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b
}

/**
 * Removes the flat white studio background from a product image by flood-filling
 * from the borders inwards. Flood filling (instead of a global colour key) keeps
 * the white areas *inside* the artwork — book pages, card faces — fully opaque.
 *
 * Returns a PNG data URL with transparency. Resolves with the original URL when
 * the image cannot be processed (e.g. the CDN blocks pixel access).
 */
export function removeWhiteBackground(src: string): Promise<string> {
  const cached = cache.get(src)
  if (cached) return cached

  const task = new Promise<string>((resolve) => {
    if (typeof document === 'undefined') {
      resolve(src)
      return
    }

    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.decoding = 'async'

    image.onerror = () => resolve(src)
    image.onload = () => {
      try {
        const { naturalWidth: width, naturalHeight: height } = image
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) {
          resolve(src)
          return
        }

        ctx.drawImage(image, 0, 0)
        const imageData = ctx.getImageData(0, 0, width, height)
        const pixels = imageData.data
        const total = width * height

        const removed = new Uint8Array(total)
        const stack = new Int32Array(total)
        let top = 0

        const push = (index: number) => {
          if (removed[index]) return
          const offset = index * 4
          if (luminance(pixels[offset], pixels[offset + 1], pixels[offset + 2]) < BG_THRESHOLD) {
            return
          }
          removed[index] = 1
          stack[top++] = index
        }

        for (let x = 0; x < width; x += 1) {
          push(x)
          push((height - 1) * width + x)
        }
        for (let y = 0; y < height; y += 1) {
          push(y * width)
          push(y * width + width - 1)
        }

        while (top > 0) {
          const index = stack[--top]
          const x = index % width
          const y = (index - x) / width

          if (x > 0) push(index - 1)
          if (x < width - 1) push(index + 1)
          if (y > 0) push(index - width)
          if (y < height - 1) push(index + width)
        }

        for (let index = 0; index < total; index += 1) {
          const offset = index * 4

          if (removed[index]) {
            pixels[offset + 3] = 0
            continue
          }

          // Soften the halo left behind by the anti-aliased outline.
          const x = index % width
          const y = (index - x) / width
          const touchesBackground =
            (x > 0 && removed[index - 1] === 1) ||
            (x < width - 1 && removed[index + 1] === 1) ||
            (y > 0 && removed[index - width] === 1) ||
            (y < height - 1 && removed[index + width] === 1)

          if (!touchesBackground) continue

          const lum = luminance(pixels[offset], pixels[offset + 1], pixels[offset + 2])
          if (lum <= EDGE_SOFT_START) continue

          const ratio = (BG_THRESHOLD - lum) / (BG_THRESHOLD - EDGE_SOFT_START)
          pixels[offset + 3] = Math.max(0, Math.min(255, Math.round(255 * ratio)))
        }

        ctx.putImageData(imageData, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } catch {
        resolve(src)
      }
    }

    image.src = src
  })

  cache.set(src, task)
  return task
}
